import { fail, ok, SimpleResult } from '@easeltv/fail';

import { obj } from '../obj';
import { Dict } from '../types';
import data from './data/iso3166.json';

/* ---------------------------------- Types --------------------------------- */

/** Union type of ISO codes */
export type ISOCountryCode = keyof typeof data;

/** Shape of a subdivision response */
export type ISOSubDivision = {
    countryCode: string;
    name: string;
    regionCode: string; // @
    code: string; // @
    type: string;
};

/** Shape of a country response with subdivisions */
export type ISOCountry = {
    name: string;
    code: string;
    subDivision: Dict<string, ISOSubDivision>;
};

/** Shape of a country response */
export type ISOCountrySimple = {
    name: string;
    code: string;
};

/* -------------------------------- Constants ------------------------------- */

/** Object containing all ISO defined countries */
const isoCountries: Dict<string, ISOCountry> = data;

/** Array of dropdown options for all ISO defined countries */
const countriesDropdownOptions = obj.entries(data).map(([, country]) => ({
    label: country.name,
    value: country.code,
}));

/* -------------------------------- Functions ------------------------------- */

/**
 * getCountry
 *
 * Return the country given the ISO-3166 code
 *
 * @param code - ISO-3166 code
 */
function getCountry(code: string): SimpleResult<ISOCountrySimple, string>;
/**
 * getCountry
 *
 * Return the country given the ISO-3166 code
 *
 * @param code - ISO-3166 code
 * @param includeSubDivisions - Flag to include the subdivisions in the response
 */
function getCountry(
    code: string,
    includeSubDivisions: true,
): SimpleResult<ISOCountry, string>;
function getCountry(
    code: string,
    includeSubDivisions?: true,
): SimpleResult<ISOCountry | ISOCountrySimple, string> {
    const result = isoCountries[code];

    if (!result) return fail('Country with that code does not exist');

    const { subDivision: _, ...country } = result;

    return ok(includeSubDivisions ? result : country);
}

/**
 * getSubDivision
 *
 * Return subdivision information given subdivision code
 *
 * @param code - Code for the sub division
 * @returns
 */
const getSubDivision = (code: string) => {
    const [countryCode, regionCode] = code.split('-');

    const country = isoCountries[countryCode];

    if (!country) return fail('Country with that code does not exist');

    const subDivision = country.subDivision[regionCode];

    if (!subDivision) return fail('Country with that code does not exist');

    return ok(subDivision);
};

/**
 * resolveIso3166Code
 *
 * Given a country code or subdivision code, return the appropriate information - country or subdivision
 *
 * @param code - Country or Subdivision code
 * @param includeSubDivisions - Flag to include subdivisions in a possible country response
 * @returns
 */
const resolveIso3166Code = (
    code: string,
    includeSubDivisions = false,
): SimpleResult<ISOCountry | ISOCountrySimple | ISOSubDivision, string> => {
    const [countryCode, regionCode] = code.toUpperCase().split('-');

    if (!regionCode) {
        return includeSubDivisions
            ? getCountry(countryCode, true)
            : getCountry(countryCode);
    }

    return getSubDivision(code);
};

/**
 * resolveIso3166Codes
 *
 * Given an array of country code or subdivision code, return the appropriate information - countries or subdivisions
 *
 * @param codes - An array of Country or Subdivision codes
 * @param includeSubDivisions - Flag to include subdivisions in a possible country response
 * @returns
 */
const resolveIso3166Codes = (
    codes: string[],
    includeSubDivisions = false,
): SimpleResult<Array<ISOCountry | ISOCountrySimple | ISOSubDivision>> =>
    ok(
        codes
            .map((code) => {
                const result = resolveIso3166Code(code, includeSubDivisions);

                if (!result.ok) {
                    // @TODO: Error handling
                    // eslint-disable-next-line no-console
                    console.log(result);

                    return undefined;
                }

                return result.payload;
            })
            .filter(Boolean) as Array<
            ISOCountry | ISOCountrySimple | ISOSubDivision
        >, // Array.filter not quite smart enough here
    );

/* --------------------------------- Export --------------------------------- */

export const lang = {
    countriesDropdownOptions,
    getCountry,
    getSubDivision,
    isoCountries,
    resolveIso3166Code,
    resolveIso3166Codes,
};
